"use client";
import { Button } from "@/components/ui/button";
import { IBlocks, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import { getTextColorForBackground, rgbToHex } from "@/lib/utils";
import AdminPopover from "../../content/AdminPopover";
import GithubUserOptions, { BlockOptions } from "./tabs/githubUser/options";
import { SettingTabs } from "../../content/SettingTabs";
import GithubUserCustomization from "./tabs/githubUser/customization";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { IconTerminal } from "@tabler/icons-react";
import BlockMetadata from "./tabs/metadata";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    newBlock?: string | null;
}
export default function BlockGithubUser({
    admin,
    user,
    block,
    update,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    let hide: string = "";
    if (!blockUpdate.options.stars) {
        hide += "stars,";
    }
    if (!blockUpdate.options.prs) {
        hide += "prs,";
    }
    if (!blockUpdate.options.issues) {
        hide += "issues,";
    }
    if (!blockUpdate.options.contribs) {
        hide += "contribs,";
    }
    let TextColour = "";
    let backgroundColor = "";
    if (blockUpdate.colour.toggle) {
        backgroundColor = rgbToHex(blockUpdate.colour.background).replace(
            "#",
            ""
        );
        TextColour = rgbToHex(blockUpdate.colour.text).replace("#", "");
    }
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "GitHub Stats",
            titleOptions: (
                <BlockOptions
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <GithubUserOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <GithubUserCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            title={`Github User - ${blockUpdate.url}`}
            onClick={handleExpand}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            {blockUpdate.url === "" ? (
                admin ? (
                    // <div className="flex flex-col items-center justify-center">
                    //     <p className="text-lg font-bold">No Username</p>
                    //     <Button onClick={handleExpand}>Add Username</Button>
                    // </div>
                    <Alert>
                        <IconTerminal
                            color="white"
                            className="text-copy h-4 w-4"
                        />
                        <AlertTitle>Oops!</AlertTitle>
                        <AlertDescription>
                            You've not added a username yet! This block will not
                            show anything until you do.
                        </AlertDescription>
                    </Alert>
                ) : null
            ) : (
                <a
                    href={"https://github.com/" + blockUpdate.url}
                    target="_blank"
                    className="w-full"
                >
                    <img
                        id="github"
                        loading="lazy"
                        style={{ borderRadius: "12px" }}
                        width="100%"
                        alt="GitHub Statistics Card"
                        src={`https://github-stats.doras.to/?username=${
                            blockUpdate.url
                        }&theme=dark&show_icons=true&bg_color=${backgroundColor}&text_color=${TextColour}&icon_color=${TextColour}&title_color=${TextColour}&hide_border=true&hide_rank=${!blockUpdate.options?.rank}&hide=${hide}`}
                    />
                </a>
            )}
        </AdminPopover>
    );
}
